import { useMemo, type ReactElement, type ReactNode } from 'react';

/**
 * Message body rendered as a controlled subset of Markdown.
 *
 * The rule that shapes this file: the content of a message is text written by
 * someone else, and it never becomes markup. It is parsed into the tree below
 * and the renderer walks that tree emitting React elements, so the only thing
 * a `<script>` or an `onerror=` in a message can ever be is a run of
 * characters inside a text node. There is deliberately no
 * `dangerouslySetInnerHTML`, no `innerHTML`, no `insertAdjacentHTML` and no
 * HTML sanitiser anywhere on this path — a sanitiser is what you need when
 * markup is being produced, and none is.
 *
 * Supported: bold, italic, strikethrough, inline code, fenced code block and
 * links. Anything else stays literal, which is the correct answer for a chat.
 */

// ---------------------------------------------------------------------------
// Tree
// ---------------------------------------------------------------------------

type InlineNode =
  | { type: 'text'; value: string }
  | { type: 'code'; value: string }
  | { type: 'link'; href: string; label: string }
  | { type: 'bold'; children: InlineNode[] }
  | { type: 'italic'; children: InlineNode[] }
  | { type: 'strike'; children: InlineNode[] };

type BlockNode =
  | { type: 'paragraph'; children: InlineNode[] }
  | { type: 'codeBlock'; value: string };

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/**
 * A fenced block, with an optional language tag captured only so it does not
 * end up inside the code. Highlighting is out of scope.
 *
 * Both patterns are built fresh on every call because `lastIndex` is mutable
 * state, and the recursion below would otherwise share it between the outer
 * and the inner scan.
 */
function fencePattern(): RegExp {
  return /```([a-zA-Z0-9+#._-]*)\r?\n?([\s\S]*?)```/g;
}

/**
 * The inline grammar, in one alternation, in this order:
 *
 *   1. inline code      — first, so its content is never interpreted
 *   2. bold             — before italic, so `**` wins over `*`
 *   3. strikethrough
 *   4. italic with `*`
 *   5. italic with `_`
 *   6. link `[label](href)`
 *   7. bare URL
 *
 * Both link forms require an explicit `http`/`https` scheme. That is what
 * keeps `javascript:` — and every other scheme that executes on click — from
 * ever reaching an `href`: no alternative matches it, so it stays text.
 */
function inlinePattern(): RegExp {
  return /`([^`\n]+)`|\*\*([\s\S]+?)\*\*|~~([\s\S]+?)~~|\*([^*\n]+)\*|_([^_\n]+)_|\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>"']*[^\s<>"'.,;:!?)\]])/g;
}

/** Bold inside italic inside a link label is already more than a chat needs. */
const MAX_DEPTH = 4;

function toInlineNode(match: RegExpExecArray, depth: number): InlineNode {
  const [, code, bold, strike, italicStar, italicUnderscore, linkLabel, linkHref, autoLink] = match;

  if (code !== undefined) {
    return { type: 'code', value: code };
  }
  if (bold !== undefined) {
    return { type: 'bold', children: parseInline(bold, depth + 1) };
  }
  if (strike !== undefined) {
    return { type: 'strike', children: parseInline(strike, depth + 1) };
  }
  if (italicStar !== undefined) {
    return { type: 'italic', children: parseInline(italicStar, depth + 1) };
  }
  if (italicUnderscore !== undefined) {
    return { type: 'italic', children: parseInline(italicUnderscore, depth + 1) };
  }
  if (linkLabel !== undefined && linkHref !== undefined) {
    return { type: 'link', href: linkHref, label: linkLabel };
  }
  if (autoLink !== undefined) {
    return { type: 'link', href: autoLink, label: autoLink };
  }

  // Unreachable while the alternation and this function agree; falling back to
  // the literal match keeps the failure harmless if they ever drift.
  return { type: 'text', value: match[0] };
}

function parseInline(source: string, depth = 0): InlineNode[] {
  if (source === '') {
    return [];
  }
  if (depth >= MAX_DEPTH) {
    return [{ type: 'text', value: source }];
  }

  const pattern = inlinePattern();
  const nodes: InlineNode[] = [];
  let cursor = 0;
  let match = pattern.exec(source);

  while (match !== null) {
    if (match.index > cursor) {
      nodes.push({ type: 'text', value: source.slice(cursor, match.index) });
    }
    nodes.push(toInlineNode(match, depth));
    cursor = match.index + match[0].length;
    match = pattern.exec(source);
  }

  if (cursor < source.length) {
    nodes.push({ type: 'text', value: source.slice(cursor) });
  }

  return nodes;
}

function parseBlocks(content: string): BlockNode[] {
  const pattern = fencePattern();
  const blocks: BlockNode[] = [];
  let cursor = 0;
  let match = pattern.exec(content);

  function pushParagraph(source: string): void {
    if (source !== '') {
      blocks.push({ type: 'paragraph', children: parseInline(source) });
    }
  }

  while (match !== null) {
    pushParagraph(content.slice(cursor, match.index));
    blocks.push({ type: 'codeBlock', value: match[2] ?? '' });
    cursor = match.index + match[0].length;
    match = pattern.exec(content);
  }

  pushParagraph(content.slice(cursor));
  return blocks;
}

// ---------------------------------------------------------------------------
// Rendering
// ---------------------------------------------------------------------------

function renderInline(nodes: InlineNode[]): ReactNode[] {
  return nodes.map((node, index) => {
    switch (node.type) {
      case 'text':
        // A plain string child: React writes it as a text node, never as markup.
        return node.value;
      case 'code':
        return (
          <code key={index} className="message-code">
            {node.value}
          </code>
        );
      case 'bold':
        return <strong key={index}>{renderInline(node.children)}</strong>;
      case 'italic':
        return <em key={index}>{renderInline(node.children)}</em>;
      case 'strike':
        return <s key={index}>{renderInline(node.children)}</s>;
      case 'link':
        return (
          <a key={index} href={node.href} target="_blank" rel="noopener noreferrer">
            {node.label}
          </a>
        );
    }
  });
}

export interface MessageContentProps {
  content: string;
}

export function MessageContent({ content }: MessageContentProps): ReactElement {
  const blocks = useMemo(() => parseBlocks(content), [content]);

  return (
    <div className="message-content">
      {blocks.map((block, index) =>
        block.type === 'codeBlock' ? (
          <pre key={index} className="message-code-block">
            <code>{block.value}</code>
          </pre>
        ) : (
          <span key={index}>{renderInline(block.children)}</span>
        ),
      )}
    </div>
  );
}

export default MessageContent;
